import { DayHours, WeeklyHours } from '../../models/asset.model';
import { GoogleOpeningHoursDto, GoogleOpeningHoursPeriod } from './google-opening-hours.types';

/** Closed day sentinel (no bookable slots in schedule-step mask). */
export const CLOSED_DAY_HOURS: DayHours = { open: '00:00', close: '00:00' };

/** End-of-day close in minutes (shown in the wizard as `00:00` = medianoche). */
export const MIDNIGHT_CLOSE_MINUTES = 24 * 60;
export const MIDNIGHT_CLOSE_LABEL = '00:00';

export function isDayClosed(d: DayHours): boolean {
  return d.open === CLOSED_DAY_HOURS.open && d.close === CLOSED_DAY_HOURS.close;
}

export function hmToMinutes(hm: string): number {
  const [h, m] = hm.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

/** Formats minutes on the 30-min wizard grid (`00:00` = medianoche / fin del día). */
export function formatMinutesToHm(mins: number): string {
  if (mins >= MIDNIGHT_CLOSE_MINUTES) {
    return MIDNIGHT_CLOSE_LABEL;
  }
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${m === 0 ? '00' : '30'}`;
}

export function dayHoursOpenToMinutes(open: string): number {
  return hmToMinutes(open);
}

/** Close as minutes; `00:00` after a real open means midnight (24:00), not closed-day sentinel. */
export function dayHoursCloseToMinutes(close: string, open: string): number {
  if (isDayClosed({ open, close })) {
    return 0;
  }
  const mins = hmToMinutes(close);
  if (mins === 0 && dayHoursOpenToMinutes(open) > 0) {
    return MIDNIGHT_CLOSE_MINUTES;
  }
  return mins;
}

export function isDayHoursRangeValid(d: DayHours): boolean {
  if (isDayClosed(d)) {
    return true;
  }
  return dayHoursCloseToMinutes(d.close, d.open) > dayHoursOpenToMinutes(d.open);
}

/** Wizard opening times: 00:30 … 23:30. */
export function buildWizardOpenTimeOptions(): string[] {
  const out: string[] = [];
  for (let m = 30; m < MIDNIGHT_CLOSE_MINUTES; m += 30) {
    out.push(formatMinutesToHm(m));
  }
  return out;
}

/** Wizard closing times after `open`, ending with `00:00` (medianoche). */
export function buildWizardCloseTimeOptions(open: string): string[] {
  const openMin = dayHoursOpenToMinutes(open);
  const out: string[] = [];
  for (let m = openMin + 30; m < MIDNIGHT_CLOSE_MINUTES; m += 30) {
    out.push(formatMinutesToHm(m));
  }
  out.push(MIDNIGHT_CLOSE_LABEL);
  return out;
}

/** Google weekday: 0 = Sunday … 6 = Saturday → our index 0 = Monday … 6 = Sunday (see schedule.constants). */
export function googleDayToWeeklyIndex(googleDay: number): number {
  return googleDay === 0 ? 6 : googleDay - 1;
}

function googleTimeToMinutes(time: string, role: 'open' | 'close'): number {
  const raw = time.replace(/\D/g, '').padStart(4, '0').slice(-4);
  const h = Number.parseInt(raw.slice(0, 2), 10);
  const m = Number.parseInt(raw.slice(2, 4), 10);

  if (h >= 24 || raw === '2400') {
    return role === 'close' ? 24 * 60 : 0;
  }

  return h * 60 + (m % 60);
}

function minutesToHm(mins: number): string {
  return formatMinutesToHm(Math.max(0, mins));
}

function snapOpen(mins: number): number {
  return Math.floor(mins / 30) * 30;
}

function snapClose(mins: number): number {
  if (mins >= MIDNIGHT_CLOSE_MINUTES) return MIDNIGHT_CLOSE_MINUTES;
  return Math.min(Math.ceil(mins / 30) * 30, MIDNIGHT_CLOSE_MINUTES - 30);
}

function* googleDaysInclusive(start: number, end: number): Generator<number> {
  let d = start;
  for (let guard = 0; guard < 8; guard++) {
    yield d;
    if (d === end) break;
    d = (d + 1) % 7;
  }
}

type DayRange = { openMin: number; closeMin: number };

function mergeDay(ranges: Array<DayRange | null>, idx: number, openMin: number, closeMin: number): void {
  const cur = ranges[idx];
  if (!cur) {
    ranges[idx] = { openMin, closeMin };
    return;
  }
  cur.openMin = Math.min(cur.openMin, openMin);
  cur.closeMin = Math.max(cur.closeMin, closeMin);
}

/** Drops same-day 00:00–09:00 blocks when a later period opens the same Google weekday. */
function isLeadingClosedGap(
  period: GoogleOpeningHoursPeriod,
  periods: GoogleOpeningHoursPeriod[],
): boolean {
  const openG = period.open?.day;
  const closeG = period.close?.day ?? openG;
  if (openG == null || closeG !== openG || !period.open?.time || !period.close?.time) {
    return false;
  }

  const openMin = googleTimeToMinutes(period.open.time, 'open');
  const closeMin = googleTimeToMinutes(period.close.time, 'close');
  if (openMin !== 0 || closeMin <= openMin) return false;

  return periods.some(p => {
    if (p === period || p.open?.day !== openG || !p.open?.time) return false;
    const laterOpen = googleTimeToMinutes(p.open.time, 'open');
    return laterOpen >= closeMin && laterOpen > 0;
  });
}

function closeMinutesForPeriod(period: GoogleOpeningHoursPeriod, openG: number, openMin: number): number {
  if (!period.close?.time) return MIDNIGHT_CLOSE_MINUTES;

  const closeG = period.close.day ?? openG;
  const closeMin = snapClose(googleTimeToMinutes(period.close.time, 'close'));

  if (closeG !== openG) {
    if (closeMin === 0) return 24 * 60;
    return closeMin;
  }

  if (closeMin === 0 && openMin > 0) return 24 * 60;
  return closeMin;
}

/**
 * Converts Google `opening_hours.periods` into our 7-day WeeklyHours.
 * Returns null when Google provides no usable schedule.
 */
export function weeklyHoursFromGoogleOpeningHours(
  openingHours?: GoogleOpeningHoursDto | null,
): WeeklyHours | null {
  const periods = openingHours?.periods;
  if (!periods?.length) return null;

  const usable = periods.filter(p => !isLeadingClosedGap(p, periods));
  const ranges: Array<DayRange | null> = Array.from({ length: 7 }, () => null);

  for (const period of usable) {
    if (period.open?.day == null || !period.open.time) continue;

    const openG = period.open.day;
    const openMin = snapOpen(googleTimeToMinutes(period.open.time, 'open'));

    if (!period.close?.time) {
      mergeDay(ranges, googleDayToWeeklyIndex(openG), openMin, MIDNIGHT_CLOSE_MINUTES);
      continue;
    }

    const closeG = period.close.day ?? openG;
    const closeMinRaw = closeMinutesForPeriod(period, openG, openMin);

    if (openG === closeG && closeMinRaw <= openMin) continue;

    const closeMin = snapClose(closeMinRaw);
    const closesAtMidnight = closeG !== openG && googleTimeToMinutes(period.close!.time!, 'close') === 0;

    for (const gDay of googleDaysInclusive(openG, closeG)) {
      if (closesAtMidnight && gDay === closeG) continue;

      const idx = googleDayToWeeklyIndex(gDay);
      if (gDay === openG && gDay === closeG) {
        mergeDay(ranges, idx, openMin, closeMin);
      } else if (gDay === openG) {
        mergeDay(ranges, idx, openMin, MIDNIGHT_CLOSE_MINUTES);
      } else if (gDay === closeG) {
        mergeDay(ranges, idx, 0, closeMin);
      } else {
        mergeDay(ranges, idx, 0, MIDNIGHT_CLOSE_MINUTES);
      }
    }
  }

  const result: WeeklyHours = [];
  let anyOpen = false;

  for (let i = 0; i < 7; i++) {
    const r = ranges[i];
    if (!r) {
      result.push({ ...CLOSED_DAY_HOURS });
      continue;
    }
    anyOpen = true;
    const openMin = r.openMin;
    let closeMin = r.closeMin;
    if (closeMin <= openMin) {
      closeMin = Math.min(openMin + 30, MIDNIGHT_CLOSE_MINUTES);
    }
    result.push({
      open: minutesToHm(openMin),
      close: minutesToHm(closeMin),
    });
  }

  return anyOpen ? result : null;
}
